import Base from '../Base';
import { getModule } from 'vuex-module-decorators';
import ComponentHelper, {
  ECanvasSidebar,
  EComponentType,
  IComponent,
  IProject,
  IScene,
  TComponentType,
  IOnboarding,
  IOnboardingSteps,
  IStreamResolution,
  IMediaInputDevices,
  TVideoMobileInputDevice,
  IImageComponent,
  IComponentListGroup,
  TEditorType,
  EEditorType,
} from '@/helpers/editor';
import { Component } from 'vue-property-decorator';
import EditorModule from '@/store/modules/editor';
import { TCanvasSidebar } from '@/helpers/editor';
import ProjectApi from '@/apis/project-api';
import { EModalName } from '@/helpers/modal';
import { cloneDeep, startCase, camelCase } from 'lodash-es';
import NotificationHelper from '@/helpers/notification';
import UserApi from '@/apis/user-api';
import { EUserFlag, IIceConfig } from '@/helpers/user';
import { ESocketEvent } from '@/helpers/socket';
import { ITheme } from '@/helpers/theme';
import { ITransition } from '@/helpers/transition';
import { ISelector } from '@/helpers/utils';
import { EAnalyticsEvent } from '@/helpers/analytic';

enum EOnboardingType {
  editor = 'editor',
  project = 'project',
}

@Component({})
export default class EditorBase extends Base {
  editorModule = getModule(EditorModule, this.$store);

  async loadProjects() {
    if (this.currentUser) {
      const response = await ProjectApi.getProjects();
      this.projects = response.projects as IProject[];

      this.activeProject = this.projects.find((project) => project.active);
      if ((this.userFlags.free_access || this.userSubscription?.is_valid) && !this.resolutionsLoaded) {
        const streamSettings = await ProjectApi.getUserStreamSettings();
        this.allowedStreamResolutions = streamSettings.settings.resolutions;
      }
    }
  }

  async loadSelectedProject(projectUuid: string) {
    const response = await ProjectApi.getProject(projectUuid);

    if ((this.userFlags.free_access || this.userSubscription?.is_valid) && !this.resolutionsLoaded) {
      const streamSettings = await ProjectApi.getUserStreamSettings();
      this.allowedStreamResolutions = streamSettings.settings.resolutions;
    }

    this.selectedProject = response.project as IProject;
  }

  get isVerticalEditor(): boolean {
    return this.selectedEditor === EEditorType.vertical;
  }

  isTextComponent(component: IComponent): boolean {
    return component.type === EComponentType.text;
  }

  isImageComponent(component: IComponent): boolean {
    return component.type === EComponentType.image;
  }

  isAlertBoxComponent(component: IComponent): boolean {
    return component.type === EComponentType.alertBox;
  }

  isCustomComponent(component: IComponent): boolean {
    return component.type === EComponentType.customComponent;
  }

  isVideoComponent(component: IComponent): boolean {
    return component.type === EComponentType.video;
  }

  isWebcamComponent(component: IComponent): boolean {
    return component.type === EComponentType.webcam;
  }

  isChatComponent(component: IComponent): boolean {
    return component.type === EComponentType.chat;
  }

  isEventListComponent(component: IComponent): boolean {
    return component.type === EComponentType.eventList;
  }

  get settingsHeader() {
    if (!this.selectedComponent) return;

    switch (this.selectedComponent.type) {
      case EComponentType.text:
        // text settings header managed in TextSettings.vue
        return null;
      case EComponentType.image:
        return { title: 'Image', icon: 'icon-image' };
      case EComponentType.alertBox:
        return { title: 'Alert Box', icon: 'icon-notifications' };
      case EComponentType.eventList:
        return { title: 'Event List', icon: 'icon-event-list' };
      case EComponentType.chat:
        return { title: 'Chat', icon: 'icon-chat-box' };
      case EComponentType.customComponent:
        return { title: 'Custom Widget', icon: 'icon-editor-7' };
      case EComponentType.webcam:
        return { title: 'Webcam', icon: 'icon-studio' };
      case EComponentType.streamLabel:
        return { title: 'Stream Label', icon: 'icon-stream-labels' };
      case EComponentType.gameCapture:
        return { title: 'Game Capture', icon: 'icon-console' };
      default:
        return null;
    }
  }

  get scale(): number {
    return this.editorModule.scale;
  }

  set scale(scale: number) {
    this.editorModule.setScale(scale);
  }

  get projectHeight(): number {
    return parseInt(this.selectedProject?.settings?.resolution) || 720;
  }

  get projectWidth(): number {
    return (this.projectHeight / 9) * 16;
  }

  get autoGoLive(): boolean {
    if (!this.selectedProject?.settings || !this.selectedProject?.settings.hasOwnProperty('auto_go_live')) {
      return true;
    }

    return !!this.selectedProject.settings.auto_go_live;
  }

  set autoGoLive(autoGoLive: boolean) {
    const settings = { settings: { ...this.selectedProject.settings, ... { auto_go_live: autoGoLive } } };
    this.selectedProject = { ...this.selectedProject, ...settings };
    this.saveProject();
  }

  get selectedComponent(): IComponent {
    return this.editorModule.selectedComponent;
  }

  set selectedComponent(component: IComponent) {
    this.editorModule.setSelectedComponent(component);
  }

  get selectedEditor(): TEditorType {
    return this.editorModule.selectedEditor;
  }

  set selectedEditor(editor: TEditorType) {
    this.editorModule.setSelectedEditor(editor);
  }

  get components(): IComponent[] {
    return this.editorModule.components;
  }

  set components(components: IComponent[]) {
    this.editorModule.setComponents(components);
  }

  get selectedScene(): IScene {
    return this.editorModule.selectedScene;
  }

  set selectedScene(scene: IScene) {
    if (!scene || !this.selectedScene || this.selectedScene.id !== scene.id) {
      this.editorModule.clearComponentUpdateList();
    }

    this.editorModule.setSelectedScene(scene);

    if (!scene) return;

    this.editorModule.setComponents(scene.components);

    const scenes = this.selectedProject.scenes.map((existingScene) => {
      if (existingScene.id === scene.id) return scene;
      return existingScene;
    });

    if (this.selectedComponent && !scene.components.some(component => component.id === this.selectedComponent.id)) {
      this.selectedComponent = null;
    }

    this.selectedProject = { ...this.selectedProject, scenes };

    if (this.$route.params.sceneUuid && this.$route.params.sceneUuid !== scene.uuid) {
      this.$router.replace({
        params: {
          sceneUuid: scene.uuid,
        }
      });
    }
  }

  get selectedProject(): IProject {
    return this.editorModule.selectedProject;
  }

  set selectedProject(project: IProject) {
    this.editorModule.setSelectedProject(project);

    // for resetting project to null
    if (!project) return;

    const projects = this.projects.map((existingProject) => {
      if (existingProject.id === project.id) return project;
      return existingProject;
    });

    this.editorModule.setProjects(projects);
  }

  get projects(): IProject[] {
    return this.editorModule.projects;
  }

  set projects(projects: IProject[]) {
    this.editorModule.setProjects(projects);
  }

  get activeProject(): IProject {
    return this.editorModule.activeProject;
  }

  set activeProject(project: IProject) {
    this.editorModule.setActiveProject(project);
  }

  get sidebarType(): TCanvasSidebar {
    return this.editorModule.sidebarType;
  }

  set sidebarType(sidebarType: TCanvasSidebar) {
    this.editorModule.setSidebarType(sidebarType);
  }

  get showSidebar(): boolean {
    return this.editorModule.showSidebar;
  }

  set showSidebar(showSidebar: boolean) {
    this.editorModule.setShowSidebar(showSidebar);
  }

  get availableFonts() {
    return this.editorModule.availableFonts;
  }

  set availableFonts(availableFonts: boolean) {
    this.editorModule.setAvailableFonts(availableFonts);
  }

  get preventComponentDeactivate() {
    return this.editorModule.preventComponentDeactivate;
  }

  set preventComponentDeactivate(preventComponentDeactivate: boolean) {
    this.editorModule.setPreventComponentDeactivate(preventComponentDeactivate);
  }

  get onboardingSteps(): IOnboardingSteps {
    return this.editorModule.onboardingSteps;
  }

  get sceneToRemove(): IScene {
    return this.editorModule.sceneToRemove;
  }

  set sceneToRemove(scene: IScene) {
    this.editorModule.setSceneToRemove(scene);
  }

  get projectToRemove(): IProject {
    return this.editorModule.projectToRemove;
  }

  set projectToRemove(project: IProject) {
    this.editorModule.setProjectToRemove(project);
  }

  get projectToEdit(): IProject {
    return this.editorModule.projectToEdit;
  }

  set projectToEdit(project: IProject) {
    this.editorModule.setProjectToEdit(project);
  }

  get streamResolutions(): IStreamResolution[] {
    return this.editorModule.streamResolutions;
  }

  get allowedStreamResolutions(): IStreamResolution[] {
    return this.editorModule.allowedStreamResolutions;
  }

  set allowedStreamResolutions(allowedStreamResolutions: IStreamResolution[]) {
    this.editorModule.setAllowedStreamResolutions(allowedStreamResolutions);
    this.editorModule.setResolutionsLoaded(true);
  }

  get resolutionsLoaded(): boolean {
    return this.editorModule.resolutionsLoaded;
  }

  get hasUnsavedChange(): boolean {
    return this.editorModule.hasUnsavedChange;
  }

  set hasUnsavedChange(hasUnsavedChange: boolean) {
    this.editorModule.setHasUnsavedChange(hasUnsavedChange);
  }

  get videoInputDevices(): IMediaInputDevices[] {
    return this.editorModule.videoInputDevices;
  }

  set videoInputDevices(videoInputDevices: IMediaInputDevices[]) {
    this.editorModule.setVideoInputDevices(videoInputDevices);
  }

  get selectedVideoInputDevice(): IMediaInputDevices {
    return this.editorModule.selectedVideoInputDevice;
  }

  set selectedVideoInputDevice(selectedVideoInputDevice: IMediaInputDevices) {
    this.editorModule.setSelectedVideoInputDevices(selectedVideoInputDevice);
  }

  get audioInputDevices(): IMediaInputDevices[] {
    return this.editorModule.audioInputDevices;
  }

  set audioInputDevices(audioInputDevices: IMediaInputDevices[]) {
    this.editorModule.setAudioInputDevices(audioInputDevices);
  }

  get selectedAudioInputDevice(): IMediaInputDevices {
    return this.editorModule.selectedAudioInputDevice;
  }

  set selectedAudioInputDevice(selectedAudioInputDevice: IMediaInputDevices) {
    this.editorModule.setSelectedAudioInputDevices(selectedAudioInputDevice);
  }

  get selectedFacingMode(): TVideoMobileInputDevice {
    return this.editorModule.selectedFacingMode;
  }

  set selectedFacingMode(selectedFacingMode: TVideoMobileInputDevice) {
    this.editorModule.setSelectedFacingMode(selectedFacingMode);
  }

  get showSaveButtonTooltip(): boolean {
    return this.editorModule.showSaveButtonTooltip;
  }

  set showSaveButtonTooltip(showSaveButtonTooltip: boolean) {
    this.editorModule.setShowSaveButtonTooltip(showSaveButtonTooltip);
  }

  get availableThemes(): ITheme[] {
    return this.editorModule.availableThemes;
  }

  set availableThemes(availableThemes: ITheme[]) {
    this.editorModule.setAvailableThemes(availableThemes);
  }

  get availableTransitions(): ITransition[] {
    return this.editorModule.availableTransitions;
  }

  set availableTransitions(availableTransitions: ITransition[]) {
    this.editorModule.setAvailableTransitions(availableTransitions);
  }

  get showCanvasGrid(): boolean {
    return this.editorModule.showCanvasGrid;
  }

  set showCanvasGrid(showCanvasGrid: boolean) {
    this.editorModule.setShowCanvasGrid(showCanvasGrid);
  }

  get themeToInstall(): ITheme {
    return this.editorModule.themeToInstall;
  }

  set themeToInstall(themeToInstall: ITheme) {
    this.editorModule.setThemeToInstall(themeToInstall);
  }

  get streamLabelData() {
    return this.editorModule.streamLabelData;
  }

  set streamLabelData(streamLabelData) {
    this.editorModule.setStreamLabelData(streamLabelData);
  }

  get streamLabelPoll() {
    return this.editorModule.streamLabelPoll;
  }

  set streamLabelPoll(streamLabelPoll) {
    this.editorModule.setStreamLabelPoll(streamLabelPoll);
  }

  get webcamStream(): MediaStream {
    return this.editorModule.webcamStream;
  }

  set webcamStream(webcamStream: MediaStream) {
    this.editorModule.setWebcamStream(webcamStream);
  }

  get iceConfig(): IIceConfig {
    return this.editorModule.iceConfig;
  }

  set iceConfig(iceConfig: IIceConfig) {
    this.editorModule.setIceConfig(iceConfig);
  }

  get showVerticalCanvas(): boolean {
    return this.editorModule.showVerticalCanvas;
  }

  set showVerticalCanvas(showVerticalCanvas: boolean) {
    this.editorModule.setShowVerticalCanvas(showVerticalCanvas);
  }

  get projectTitle(): string {
    return this.selectedProject?.title;
  }

  get sceneTitle(): string {
    return this.selectedScene?.title;
  }

  get selectedProjectHasActiveScene(): boolean {
    return !!this.selectedProject?.active_scene_id;
  }

  get selectedProjectActiveScene(): IScene {
    return this.selectedProject?.active_scene;
  }

  get editorOnboardingSteps(): IOnboarding[] {
    return [
      {
        step: 1,
        title: 'Add Component',
        description:
          'Click here to add more customization to your scene.  You can change themes, add an Alert box widget, texts, or images.',
        buttonText: 'Next',
        videoSrc: 'https://cdn.streamlabsstudio.com/static/videos/onboarding/step3-addlayers.mp4',
      },
      {
        step: 2,
        title: 'Add scene',
        description:
          'Click here to add more scenes that you can switch between during your stream.',
        videoSrc: 'https://cdn.streamlabsstudio.com/static/videos/onboarding/step4-addscene.mp4',
      },
    ];
  }

  get projectOnboardingSteps(): IOnboarding[] {
    return [
      {
        step: 1,
        title: 'Start by selecting the default Scene Collection',
        description:
          "Check out this Default project to see how it's set up. You can customize scene or just go live!",
        buttonText: 'Next',
        videoSrc: 'https://cdn.streamlabsstudio.com/static/videos/onboarding/step1-chooseproject.mp4',
      },
      {
        step: 2,
        title: 'Or create your own Scene Collection',
        description: 'Add as many fully customizable scenes as you want.',
        videoSrc: 'https://cdn.streamlabsstudio.com/static/videos/onboarding/step2-createproject.mp4',
      },
    ];
  }

  get mobileStreamContainerWidth(): number {
    return Math.min(window.innerWidth - 32, 320);
  }

  get mobileStreamContainerHeight(): number {
    return (this.mobileStreamContainerWidth / 16) * 9;
  }

  get mobileStreamContainerY(): number {
    return window.innerHeight - this.mobileStreamContainerHeight - 16;
  }

  get appliedTheme(): ITheme {
    if (!this.selectedScene?.theme_id || !this.availableThemes) return null;

    return this.availableThemes.find((theme) => theme.id === this.selectedScene.theme_id);
  }

  get appliedThemeName(): string {
    if (!this.appliedTheme) return null;

    return this.appliedTheme.name;
  }

  get componentListGroup(): IComponentListGroup {
    const simpleComponents = [];
    const streamlabsComponents = [];
    Object.keys(EComponentType)
      .forEach((key: TComponentType) => {
        if (EComponentType.video === EComponentType[key]) return;

        const label = EComponentType.customComponent === EComponentType[key] ? 'Custom Widget' : startCase(key);
        const iconClass = this.getLayerIcon(key);

        const item: ISelector = {
          label,
          value: EComponentType[key],
          iconClass,
        };

        if (this.componentIsUnique(EComponentType[key]) && this.components?.length) {
          if (this.components.map((component) => component.type).includes(EComponentType[key])) {
            item.disabled = true;
            // for multiselect
            item['$isDisabled'] = true;
          }
        }

        this.componentRequiresSLAccount(EComponentType[key]) ? streamlabsComponents.push(item) : simpleComponents.push(item);
      });

    return { simpleComponents, streamlabsComponents };
  }

  getLayerIcon(componentType) {
    return ComponentHelper.getComponentIcon(componentType);
  }

  getComponentRefName(component: IComponent) {
    return `${component.type}-${component.id}`;
  }

  async onComponentUpdate(component: Partial<IComponent>, shouldTriggerUnsaved: boolean = true) {
    if (!this.hasUnsavedChange && shouldTriggerUnsaved) this.hasUnsavedChange = true;

    if (shouldTriggerUnsaved) {
      await this.addComponentUpdateUndo();
    }

    let currentComponent;

    this.components = this.components.map((comp: IComponent) => {
      if (comp.id === component.id) {
        currentComponent = { ...comp };

        // Update non-verticalSettings properties
        for (const key in component) {
          if (key !== 'verticalSettings') {
            currentComponent[key] = component[key];
          }
        }

        // Update verticalSettings if it exists in component
        if (component.verticalSettings) {
          currentComponent.verticalSettings = {
            ...comp.verticalSettings, // Preserve existing verticalSettings
            ...component.verticalSettings, // Overwrite with new values
          };

          // if missing position data, set it to the current position
          if (
            !currentComponent.verticalSettings.x &&
            !currentComponent.verticalSettings.y &&
            !currentComponent.verticalSettings.w &&
            !currentComponent.verticalSettings.h
          ) {
            const defaultVerticalSettings =
              this.getVerticalSettingsPosition(currentComponent);
            currentComponent.verticalSettings = {
              ...currentComponent.verticalSettings,
              ...defaultVerticalSettings,
            };
          }
        }

        return currentComponent;
      }
      return comp;
    });

    if (shouldTriggerUnsaved) this.selectedComponent = currentComponent;
  }

  async addComponentUpdateUndo() {
    this.editorModule.addComponentUpdateUndo(cloneDeep(this.components));
  }

  async undoComponentUpdate() {
    this.hasUnsavedChange = true;
    this.editorModule.undoComponentUpdateAction();
  }

  async redoComponentUpdate() {
    this.hasUnsavedChange = true;
    this.editorModule.redoComponentUpdateAction();
  }

  openUploadManager(shouldReplaceExisting = null) {
    this.preventComponentDeactivate = true;
    this.$modal.show(EModalName.uploadManagerMobile, { shouldReplaceExisting });
  }

  openCodeEditor() {
    this.$modal.show(EModalName.codeEditor);
  }

  openSidebar(sidebarType: TCanvasSidebar) {
    if (sidebarType === ECanvasSidebar.settings) {
      this.preventComponentDeactivate = true;
    }

    if (this.sidebarType === sidebarType) {
      this.toggleSidebar();
      return;
    }

    this.sidebarType = sidebarType;
    this.showSidebar = true;
  }

  toggleSidebar() {
    this.showSidebar = !this.showSidebar;
  }

  async createScene(projectUuid: string, sceneName: string): Promise<IScene> {
    const components: IComponent[] = [];

    // game capture should always exist
    const gameCaptureComponent = ComponentHelper.defaultGameCaptureSettings;
    gameCaptureComponent.w = this.projectWidth;
    gameCaptureComponent.h = this.projectHeight;

    components.push(gameCaptureComponent);

    const postData: IScene = {
      title: sceneName,
      settings: {},
      components,
    };

    const response = await ProjectApi.createProjectScene(projectUuid, postData);

    return response.scene as IScene;
  }

  addComponent(type: TComponentType) {
    if (!this.hasUnsavedChange) this.hasUnsavedChange = true;
    const components = cloneDeep(this.components);
    let component;

    switch (type) {
      case EComponentType.alertBox:
      case EComponentType.eventList:
      case EComponentType.chat:
      case EComponentType.streamLabel:
        if (!this.userSLWidgetToken) {
          this.$modal.show(EModalName.connectStreamlabsModal);
        }

        component = ComponentHelper.getComponentSettingsByType(type);

        this.$sla.sendEvent(EAnalyticsEvent.streamlabsWidgetAdded, { type });
        break;
      case EComponentType.text:
        component = ComponentHelper.defaultTextSettings;
        component.newlyAdded = true;
        this.$sla.sendEvent(EAnalyticsEvent.otherComponentAdded, { type });

        break;
      case EComponentType.image:
        this.openUploadManager();
        this.$sla.sendEvent(EAnalyticsEvent.otherComponentAdded, { type });

        return;
      case EComponentType.video:
        break;
      // this.$sla.sendEvent(EAnalyticsEvent.otherComponentAdded, { type });
      // component = ComponentHelper.defaultVideoSettings;
      case EComponentType.customComponent:
        component = ComponentHelper.defaultCustomComponentSettings;
        this.$sla.sendEvent(EAnalyticsEvent.otherComponentAdded, { type });

        break;
      case EComponentType.webcam:
        component = ComponentHelper.defaultWebcamSettings;
        this.$sla.sendEvent(EAnalyticsEvent.webcamAdded);

        break;
      case EComponentType.gameCapture:
        component = ComponentHelper.defaultGameCaptureSettings;
        component.w = this.projectWidth;
        component.h = this.projectHeight;
        this.$sla.sendEvent(EAnalyticsEvent.otherComponentAdded, { type });

        break;
    }

    if (component.verticalSettings?.useFullWidth) {
      const width = this.projectHeight;
      const height = component.verticalSettings.h * (width / component.verticalSettings.w);

      component.verticalSettings.w = width;
      component.verticalSettings.h = height;
    }

    components.unshift(component);

    if (component.sortIndex) {
      components.sort((componentA, componentB) => {
        const sortIndexA = componentA.sortIndex ?? 999;
        const sortIndexB = componentB.sortIndex ?? 999;

        return sortIndexB - sortIndexA;
      });
    }

    this.editorModule.addComponentUpdateUndo(cloneDeep(this.components));
    this.components = components;
    this.selectedComponent = component;
  }

  removeComponent(componentToRemove = this.selectedComponent) {
    if (!this.hasUnsavedChange) this.hasUnsavedChange = true;

    this.editorModule.addComponentUpdateUndo(cloneDeep(this.components));

    const components = this.components.filter(
      (component) => componentToRemove.id !== component.id,
    );

    switch (componentToRemove.type) {
      case EComponentType.webcam:
        this.$sla.sendEvent(EAnalyticsEvent.webcamRemoved);
        break;
      case EComponentType.alertBox:
      case EComponentType.eventList:
      case EComponentType.chat:
      case EComponentType.streamLabel:
        this.$sla.sendEvent(EAnalyticsEvent.streamlabsWidgetRemoved, { type: componentToRemove.type });
        break;
      default:
        this.$sla.sendEvent(EAnalyticsEvent.otherComponentRemoved, { type: componentToRemove.type });
        break;
    }

    if (componentToRemove.id === this.selectedComponent?.id) {
      this.selectedComponent = null;
    }

    this.components = components;
  }

  toggleHide(component = this.selectedComponent, editorType = this.selectedEditor) {
    const data: Partial<IComponent> = {
      id: component.id,
    };

    if (editorType === EEditorType.vertical) {
      const verticalSettings = { hidden: !component.verticalSettings?.hidden };

      data.verticalSettings = { ...component.verticalSettings, ...verticalSettings };
    } else {
      data.hidden = !component.hidden;
    }

    this.onComponentUpdate(data);
  }

  toggleHideHorizontally(component = this.selectedComponent) {
    this.toggleHide(component, EEditorType.horizontal);
  }

  toggleHideVertically(component = this.selectedComponent) {
    this.toggleHide(component, EEditorType.vertical);
  }

  toggleLock(component = this.selectedComponent) {
    const data: Partial<IComponent> = {
      id: component.id,
      locked: !component.locked,
    };

    this.onComponentUpdate(data);
  }

  async saveScene() {
    this.resetUnsavedChange();

    this.selectedScene = { ...this.selectedScene, ...{ components: this.components } };

    const data = {
      settings: {},
      components: this.components,
      theme_id: this.selectedScene.theme_id,
    };

    const response = await ProjectApi.saveProjectScene(
      this.selectedProject.uuid,
      this.selectedScene.uuid,
      data,
    );

    if (response.success) {
      if (this.selectedProject?.active_scene_id === this.selectedScene?.id) {
        // remove after testing
        const gameCapture = this.components.find((component) => {
          return component.type === EComponentType.gameCapture;
        });

        let payload = {
          type: 2,
          pos_x: 0,
          pos_y: 0,
          w: this.projectWidth,
          h: this.projectHeight
        };

        if (gameCapture) {
          payload = {
            type: 2,
            pos_x: gameCapture.x,
            pos_y: gameCapture.y,
            w: gameCapture.w,
            h: gameCapture.h
          };
        }

        this.sendSocketEvent(ESocketEvent.updateGameCapture, payload);
        this.sendSocketEvent(ESocketEvent.updateScene);
      }

      const notificationContainer = document.getElementsByClassName('top-nav-notification-container');

      if (notificationContainer) {
        NotificationHelper.success('Scene is saved successfully', '.top-nav-notification-container');
      } else {
        NotificationHelper.success('Scene is saved successfully');
      }

    } else {
      NotificationHelper.error(response.message);
    }
  }

  async saveProject(saveScene = true) {
    const data: Partial<IProject> = {
      settings: this.selectedProject.settings,
      title: this.selectedProject.title,
      scene_transition_id: this.selectedProject.scene_transition_id,
    };

    if (saveScene) data.scenes = this.selectedProject.scenes;

    const response = await ProjectApi.saveProject(
      this.selectedProject.uuid,
      data
    );

    if (response.success) {
      if (saveScene)
        NotificationHelper.success('Project updated successfully');
    } else {
      NotificationHelper.error(response.message);
    }
  }

  openPreview() {
    const route = this.$router.resolve({
      path: `/overlay/${this.currentUser.identifier}?preview=true`,
    });

    window.open(route.href, '_blank');
  }

  async setSceneAsActive(scene: IScene) {
    if (!scene || (this.selectedProject && this.selectedProject.active_scene_id === scene.id)) return;

    const response = await ProjectApi.setSceneAsActive(this.selectedProject.uuid, scene.uuid);

    if (response.success) {
      const activeScene = this.selectedProject.scenes.find((sceneInSelectedProject) => sceneInSelectedProject.id === scene.id);
      this.selectedProject = { ...this.selectedProject, active_scene_id: scene.id, active_scene: activeScene };
      const gameCapture = activeScene.components.find((component) => {
        return component.type === EComponentType.gameCapture;
      });

      const gameCaptureDefaultSettings = ComponentHelper.getComponentSettingsByType(gameCapture.type);

      const gameCapturePayload = {
        pos_x: gameCapture?.x || 0,
        pos_y: gameCapture?.y || 0,
        w: gameCapture?.w || this.projectWidth,
        h: gameCapture?.h || this.projectHeight,
        verticalSettings: {
          pos_x: gameCapture?.verticalSettings?.x || gameCaptureDefaultSettings.verticalSettings?.x || 0,
          pos_y: gameCapture?.verticalSettings?.y || gameCaptureDefaultSettings.verticalSettings?.y || 0,
          w: gameCapture?.verticalSettings?.w || gameCaptureDefaultSettings.verticalSettings?.w || this.projectHeight,
          h: gameCapture?.verticalSettings?.h || gameCaptureDefaultSettings.verticalSettings?.h || Math.round(this.projectHeight / gameCaptureDefaultSettings.aspectRatio),
        }
      };

      this.sendSocketEvent(ESocketEvent.updateGameCapture, gameCapturePayload);
      this.sendSocketEvent(ESocketEvent.updateScene);
      if (this.isLive) this.$sla.sendEvent(EAnalyticsEvent.activeSceneChanged);
    }
  }

  async removeScene() {
    if (!this.sceneToRemove) return;
    await ProjectApi.deleteProjectScene(this.selectedProject.uuid, this.sceneToRemove.uuid);

    const scenes: Partial<IProject> = {
      scenes: this.selectedProject.scenes.filter((scene) => this.sceneToRemove.id !== scene.id),
    };

    this.selectedProject = { ...this.selectedProject, ...scenes };

    if (this.selectedProject.active_scene_id === this.sceneToRemove.id) {
      this.setSceneAsActive(this.selectedProject.scenes[0]);
    }

    this.selectedScene = this.selectedProject.scenes[0];
    this.sceneToRemove = null;
    this.selectedComponent = null;
    this.$sla.sendEvent(EAnalyticsEvent.sceneDeleted);
  }

  async removeProject() {
    if (!this.projectToRemove) return;
    if (this.selectedProject) this.selectedProject = null;
    await ProjectApi.deleteProject(this.projectToRemove.uuid);
    this.projects = this.projects.filter((project) => this.projectToRemove.id !== project.id);

    this.projectToRemove = null;
    this.$sla.sendEvent(EAnalyticsEvent.projectDeleted);

    if (this.$route.path !== '/projects') this.$router.push('/projects');
  }

  openSceneRemovalConfirmationModal(scene: IScene) {
    this.sceneToRemove = scene;
    this.$modal.show(this.sceneRemovalConfirmationModalName);
  }

  get sceneRemovalConfirmationModalName(): string {
    return EModalName.sceneDeleteConfirmation;
  }

  get sceneRemovalConfirmationTitle(): string {
    return `Delete "${this.sceneToRemove?.title || ''}"`;
  }

  get sceneRemovalConfirmationText(): string {
    return `Are you sure you want to delete the scene ${this.sceneToRemove?.title || ''
      }?  This action cannot be undone.`;
  }

  openProjectRemovalConfirmationModal(project: IProject) {
    this.projectToRemove = project;
    this.$modal.show(this.projectRemovalConfirmationModalName);
  }

  get projectRemovalConfirmationModalName(): string {
    return EModalName.projectDeleteConfirmation;
  }

  get projectRemovalConfirmationTitle(): string {
    return `Delete "${this.projectToRemove?.title || ''}"`;
  }

  get projectRemovalConfirmationText(): string {
    return `Are you sure you want to delete the project ${this.projectToRemove?.title || ''
      }?  This action cannot be undone.`;
  }

  get confirmationModalWidth(): number {
    if (this.isMobile) return 343;
    return 400;
  }

  get unsavedChangeConfirmationModalName(): string {
    return EModalName.unsavedChangeConfirmation;
  }

  openUnsavedChangeConfirmation(params) {
    this.$modal.show(this.unsavedChangeConfirmationModalName, params);
  }

  onNavigateAwayWithUnsavedChange(callback, callbackData?) {
    if (this.hasUnsavedChange) {
      this.openUnsavedChangeConfirmation({
        onConfirmCallback: callback,
        callbackData,
      });

      return;
    }

    callback(callbackData);
  }

  resetUnsavedChange() {
    this.hasUnsavedChange = false;
  }

  async onUnsavedChangeConfirm() {
    await this.saveScene();
    this.resetUnsavedChange();
  }

  async resetComponentToDefault() {
    let component = this.selectedComponent;
    if (this.isImageComponent(this.selectedComponent)) {
      const imgSizeAndAspectRatio = await ComponentHelper.getImageOriginalSizeAndAspectRatio(component.value, this.projectWidth);
      component = {
        ...component,
        x: 0,
        y: 0,
        w: imgSizeAndAspectRatio.width,
        h: imgSizeAndAspectRatio.height,
        aspectRatio: imgSizeAndAspectRatio.aspectRatio,
        lockAspectRatio: true,
      };
    } else {
      const settingName = `default${startCase(camelCase(this.selectedComponent.type)).replace(/ /g, '')}Settings`;
      component = ComponentHelper[settingName];
      component.id = this.selectedComponent.id;

      if (this.selectedComponent.type === EComponentType.gameCapture) {
        component.w = this.projectWidth;
        component.h = this.projectHeight;
      }
    }

    this.onComponentUpdate({ ...this.selectedComponent, ...component });
  }

  advanceOnboardingStep(key: EOnboardingType, value: number = 1) {
    if (key === 'project' && this.onboardingSteps[key] === 2 && value) {
      UserApi.setUserProjectOnboardingShown();
    }

    if (key === 'editor' && this.onboardingSteps[key] === 2 && value) {
      UserApi.setUserEditorOnboardingShown();
    }

    this.editorModule.setOnboardingSteps({ key, value });
  }

  async openCheckoutModalMiniOnUserFlag() {
    if (
      this.userFlags &&
      this.userFlags.hasOwnProperty(EUserFlag.streamAttempt) &&
      !this.isSubscriber
    ) {
      this.openCheckoutModalMini(null);

      this.removeUserFlag(EUserFlag.streamAttempt);

      await UserApi.removeStreamAttemptFlag();
    }
  }

  componentIsUnique(componentType: TComponentType): boolean {
    return ComponentHelper.isUniqueComponents.includes(componentType);
  }

  componentRequiresSLAccount(componentType: TComponentType): boolean {
    return ComponentHelper.isSLComponents.includes(componentType);
  }

  async maybeDisplaySaveButtonTooltip() {
    if (this.selectedProject?.settings.newlyAdded) {
      this.showSaveButtonTooltip = true;
      const newSettings = cloneDeep(this.selectedProject.settings);
      delete newSettings.newlyAdded;
      this.selectedProject = { ...this.selectedProject, ...{ settings: newSettings } };
      await ProjectApi.saveProject(this.selectedProject.uuid, this.selectedProject);
    }
  }

  dismissSaveButtonTooltip() {
    this.showSaveButtonTooltip = false;
  }

  openThemeLibrary() {
    this.$modal.show(EModalName.themeLibrary);
  }

  refreshOverlayPage() {
    this.sendSocketEvent(ESocketEvent.refreshOverlay);
  }

  setHasUnsavedChange(hasUnsavedChange: boolean) {
    this.hasUnsavedChange = hasUnsavedChange;
  }

  async removeTheme() {
    const resetComponents = cloneDeep(this.components).reduce((resetComponents, component) => {
      if (component.type === EComponentType.image && (component as IImageComponent).isBorder) return resetComponents;
      if (component.type === EComponentType.alertBox) {
        component.w = 800;
        component.h = 600;
        component.x = 0;
        component.y = 0;
      }
      resetComponents.push(component);

      return resetComponents;
    }, []);

    this.components = resetComponents;

    const scene = cloneDeep(this.selectedScene);
    scene.theme_id = null;
    scene.components = this.components;
    this.selectedScene = scene;
    this.hasUnsavedChange = true;

    NotificationHelper.success('Theme reset!  Save the scene to update the overlay.');
  }

  async deleteSceneTransition() {
    this.selectedProject = { ...this.selectedProject, ...{ scene_transition_id: null, scene_transition: null } };
    await this.saveProject(false);

    this.$socket.client.emit(ESocketEvent.updateTransition, null);
  }


  async getIceServerConfig(userIdentifier: string) {
    try {
      if (this.iceConfig) {
        const currentTime = new Date().getTime();
        // give 1 min margin of error
        const expiresAtTimeMinusOneMin = new Date(this.iceConfig.expires_at).getTime() - 60000;
        if (expiresAtTimeMinusOneMin > currentTime) return;
      }

      const iceResponse = await UserApi.getIceConfig(userIdentifier);

      this.iceConfig = iceResponse.config;
    } catch (_error) {
    }
  }

  getVerticalSettingsPosition(component: IComponent) {
    if (this.isImageComponent(component) && (component as IImageComponent).isBorder) {
      return {
        x: 0,
        y: 0,
        w: this.projectHeight,
        h: this.projectWidth,
        lockAspectRatio: false,
        locked: true,
      };
    }

    const defaultSettings = ComponentHelper.getComponentSettingsByType(component.type);

    return {
      x: Math.round(component.verticalSettings?.x ?? defaultSettings?.verticalSettings?.x ?? 0),
      y: Math.round(component.verticalSettings?.y ?? defaultSettings?.verticalSettings?.y ?? 0),
      w: Math.round(component.verticalSettings?.w ?? defaultSettings?.verticalSettings?.w ?? component.w),
      h: Math.round(component.verticalSettings?.h ?? defaultSettings?.verticalSettings?.h ?? component.h),
      lockAspectRatio: component.verticalSettings?.lockAspectRatio ?? defaultSettings.lockAspectRatio ?? false,
      aspectRatio: component.verticalSettings?.aspectRatio ?? defaultSettings.aspectRatio ?? 16 / 9,
    };
  }

  get selectedComponentCanvasSettings(): Partial<IComponent> {
    if (this.isVerticalEditor) {
      const position = this.getVerticalSettingsPosition(this.selectedComponent);

      return {
        ...this.selectedComponent.verticalSettings,
        ...position,
      };
    }

    return this.selectedComponent;
  }

  openStreamInfoModal() {
    this.$modal.show(EModalName.streamInfo);
  }
}
